/**
 * ProjectTemplatesListUI - Presentational component for project templates list
 *
 * This component handles only UI rendering. All API calls and business logic
 * should be handled by the parent shell component.
 */

import { CheckIcon, PlusIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import * as Yup from 'yup';
import type { GetProjectTemplatesResponse } from '@/api/Sherpa/ProjectTemplate/types';
import Button from '@/components/UI/Button';
import { FilterBadge } from '@/components/UI/FilterBadge';
import Input from '@/components/UI/Form/Input';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import LoadingTable from '@/components/UI/loading/LoadingTable';
import Modal from '@/components/UI/Modal';
import Confirm from '@/components/UI/Modal/Confirm';
import Table from '@/components/UI/Table';
import { constructionType, projectType } from '@/constants/Project';
import type { ReactSelectOption } from '@/types/Constants';
import type { ProjectTemplateInterface } from '../../../types';
import EditableCell from '../../EditableCell';
import type { FilterParams, ProjectTemplatesListUIProps } from './types';

type FilterFormFields = Pick<FilterParams, 'name' | 'constructionType' | 'projectType'>;

const validationSchemaForFilter: Yup.ObjectSchema<FilterFormFields> = Yup.object({
  name: Yup.string().optional().max(50, 'Template name must be less than 50 characters'),
  constructionType: Yup.object({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }).optional(),
  projectType: Yup.object({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }).optional(),
});

export const ProjectTemplatesListUI = ({
  templates,
  isLoading,
  pageCount,
  currentPage,
  pageSize,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  onEdit,
  onDelete,
  onFilter,
  onClearFilter,
  isDeleting,
  isUpdating,
  hasPermission,
  filterParams,
  templateDetailBasePath = '',
}: ProjectTemplatesListUIProps) => {
  const baseId = useId();
  const templateNameRef = useRef<HTMLInputElement | null>(null);
  const templateDescriptionRef = useRef<HTMLInputElement | null>(null);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputError, setInputError] = useState({
    name: '',
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplateInterface>({
    id: 0,
    name: '',
    description: '',
    constructionType: '',
    projectType: '',
  });
  const [data, setData] = useState<GetProjectTemplatesResponse[]>([]);
  const [_skipPageReset, setSkipPageReset] = useState(false);

  const { projectType: selectedProjectType, name, constructionType: selectedConstructionType } = filterParams;

  useEffect(() => {
    setData(templates);
  }, [templates]);

  const updateTableData = (rowIndex: number, columnId: string, value: string) => {
    setSkipPageReset(true);
    setData((old: GetProjectTemplatesResponse[]) =>
      old.map((row: GetProjectTemplatesResponse, index: number) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const cancelUpdate = () => {
    setData(templates);
  };

  const handleSave = (template: ProjectTemplateInterface) => {
    const { id, name, description, constructionType, projectType } = template;
    const updatedData = {
      id,
      name,
      description,
      constructionType,
      projectType,
    };
    onEdit(updatedData);
    setIsEditing(false);
  };

  const submitFilter = (values: FilterParams) => {
    onFilter(values);
    setShowFilterModal(false);
  };

  const handleFilterClear = () => {
    onClearFilter();
    setShowFilterModal(false);
  };

  let tableColumns: ColumnDef<any>[] = [
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const nameValue = getValue() as string;
        const template = row.original;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (!hasPermission(['READ_PROJECT_TEMPLATE'])) {
          return <span>{nameValue}</span>;
        }
        if (editableRowIndex !== row.index) {
          return (
            <p className='underline hover:text-primaryColor-normal transition duration-300 ease-in-out w-52'>
              <Link to={`${templateDetailBasePath}${template.id}`}>{nameValue}</Link>
            </p>
          );
        } else {
          return (
            <div className='relative'>
              <Input
                ref={templateNameRef}
                type='text'
                name={template.id}
                defaultValue={template.name}
                customClassName='w-60 xl:w-72'
                placeholder='e.g. Office building template'
                maxLength={50}
                onBlur={() => {
                  if (templateNameRef?.current?.value) {
                    updateTableData(row.index, column.id, templateNameRef.current.value);
                    setInputError((prevState) => ({
                      ...prevState,
                      name: '',
                    }));
                  } else {
                    updateTableData(row.index, column.id, '');
                    setInputError((prevState) => ({
                      ...prevState,
                      name: 'This is required',
                    }));
                  }
                }}
                error={inputError.name}
              />
              <span className='text-xs absolute'>50 Characters</span>
            </div>
          );
        }
      },
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const descriptionValue = getValue() as string;
        const template = row.original;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (editableRowIndex !== row.index) {
          return <p className='max-w-md break-words line-clamp-3'>{descriptionValue}</p>;
        } else {
          return (
            <div className='w-72 xl:w-96 relative'>
              <Input
                ref={templateDescriptionRef}
                type='text'
                name={template.id}
                defaultValue={template.description}
                maxLength={250}
                onBlur={() => {
                  if (templateDescriptionRef?.current?.value) {
                    updateTableData(row.index, column.id, templateDescriptionRef.current.value);
                  } else {
                    updateTableData(row.index, column.id, '');
                  }
                }}
              />
              <span className='text-xs absolute'>250 Characters</span>
            </div>
          );
        }
      },
    },
    {
      id: 'constructionType',
      header: 'Construction Type',
      accessorKey: 'constructionType',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const constructionTypeValue = getValue() as string;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (editableRowIndex !== row.index) {
          return <p className='w-36'>{constructionTypeValue}</p>;
        } else {
          return (
            <div className='w-48 xl:w-60'>
              <ReactSelect
                id={`${baseId}-constructionType-${row.index}`}
                name='constructionType'
                defaultValue={{
                  label: constructionTypeValue,
                  value: constructionTypeValue,
                }}
                options={constructionType}
                onChange={(option) => {
                  const reactSelectOption = option as ReactSelectOption;
                  updateTableData(row.index, column.id, reactSelectOption.value);
                }}
              />
            </div>
          );
        }
      },
    },
    {
      id: 'projectType',
      header: 'Project Type',
      accessorKey: 'projectType',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const projectTypeValue = getValue() as string;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (editableRowIndex !== row.index) {
          return <p className='w-36'>{projectTypeValue}</p>;
        } else {
          return (
            <div className='w-48 xl:w-60'>
              <ReactSelect
                id={`${baseId}-projectType-${row.index}`}
                name='projectType'
                defaultValue={{
                  label: projectTypeValue,
                  value: projectTypeValue,
                }}
                options={projectType}
                onChange={(option) => {
                  const reactSelectOption = option as ReactSelectOption;
                  updateTableData(row.index, column.id, reactSelectOption.value);
                }}
              />
            </div>
          );
        }
      },
    },
    {
      id: 'createdBy',
      header: 'Created By',
      accessorKey: 'createdBy',
      cell: (props) => {
        const { getValue } = props;
        const createdByValue = getValue() as { name: string } | undefined;
        return <p className='w-36'>{createdByValue?.name ?? 'none'}</p>;
      },
    },
    {
      id: 'edit',
      header: '',
      accessorKey: 'edit',
      size: 36,
      cell: (props) => {
        const { row, table } = props;
        const template = row.original;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;
        const setEditableRowIndex = meta?.setEditableRowIndex;

        if (editableRowIndex !== row.index) {
          return (
            <div className='text-neutralColor-normal flex gap-4'>
              {hasPermission(['UPDATE_PROJECT_TEMPLATE']) && (
                <span
                  onClick={() => {
                    setEditableRowIndex?.(row.index);
                    setIsEditing(true);
                  }}
                >
                  <PencilIcon className='h-5 w-5 hover:text-primaryColor-normal transition duration-300 ease-in-out' />
                </span>
              )}
              {hasPermission(['DELETE_PROJECT_TEMPLATE']) && (
                <span
                  onClick={() => {
                    setSelectedTemplate(template);
                    setShowDeleteConfirm(true);
                  }}
                >
                  <TrashIcon className='h-5 w-5 hover:text-tertiaryColor-normal transition duration-300 ease-in-out' />
                </span>
              )}
            </div>
          );
        } else {
          return (
            <div className='flex justify-center gap-4'>
              <span
                className='text-primaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-1 transition duration-300 ease-in-out'
                onClick={() => {
                  if (inputError.name.length === 0) {
                    setEditableRowIndex?.(null);
                    handleSave({
                      ...row.original,
                      id: row.original.id,
                    });
                  }
                }}
              >
                <CheckIcon className='h-4 w-4' />
              </span>
              <span
                className='h-6 w-6 text-tertiaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-1 transition duration-300 ease-in-out'
                onClick={() => {
                  setEditableRowIndex?.(null);
                  setIsEditing(false);
                  cancelUpdate();
                }}
              >
                <XCircleIcon className='h-4 w-4' />
              </span>
            </div>
          );
        }
      },
    },
  ];

  if (isEditing) {
    tableColumns = tableColumns.filter((c) => c.id !== 'createdBy' && c.id !== 'updatedAt');
  }

  const defaultColumn: Partial<import('@tanstack/react-table').ColumnDef<any>> = {
    cell: EditableCell,
  };

  const tablePagination = {
    pageCount,
    pageSize,
    currentPage,
    page: currentPage,
    setPageSize: onPageSizeChange,
    nextPage: onNextPage,
    previousPage: onPreviousPage,
  };

  const confirmDeleteProjectTemplate = async () => {
    if (selectedTemplate.id === 0) {
      return;
    }
    onDelete(selectedTemplate.id);
    setShowDeleteConfirm(false);
  };

  const cancelDeleteProjectTemplate = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div className='flex flex-wrap gap-2 mb-4 mx-4'>
        <button
          className='p-2 rounded-md bg-gray-100 flex gap-1 items-center hover:bg-neutralColor-light cursor-pointer text-xs font-medium transition duration-300 ease-in-out'
          onClick={() => setShowFilterModal(true)}
        >
          <PlusIcon className='h-4 w-4' />
          <span>Add Filter</span>
        </button>
        {name && (
          <FilterBadge
            title={`Name: ${name}`}
            onClear={() => {
              onFilter({ ...filterParams, name: '' });
            }}
          />
        )}
        {selectedConstructionType && (
          <FilterBadge
            onClear={() => {
              onFilter({ ...filterParams, constructionType: undefined });
            }}
            title={`Construction Type: ${selectedConstructionType.label}`}
          />
        )}
        {selectedProjectType && (
          <FilterBadge
            onClear={() => {
              onFilter({ ...filterParams, projectType: undefined });
            }}
            title={`Project Type: ${selectedProjectType.label}`}
          />
        )}
      </div>
      {data && (
        <Table
          cols={tableColumns}
          defaultColumn={defaultColumn}
          tableData={data}
          updateTableData={updateTableData}
          pagination={tablePagination}
        />
      )}
      {(!data || isLoading) && <LoadingTable />}

      {showDeleteConfirm && (
        <Confirm
          title='Delete Template'
          message={`You are about to delete project template ${selectedTemplate.name}. Are you sure you want to delete ?`}
          acceptButtonLabel={`${isDeleting ? 'Deleting...' : 'Delete'}`}
          declineButtonLabel='Cancel'
          accept={confirmDeleteProjectTemplate}
          decline={cancelDeleteProjectTemplate}
        />
      )}
      {showFilterModal && (
        <Modal customStyle='overflow-visible'>
          <h2 className='m-0 text-primaryColor-normal'>Project Template Filter</h2>
          <HookFormProvider defaultValues={filterParams} resolver={yupResolver(validationSchemaForFilter)}>
            <HookForm onSubmit={submitFilter} className='space-y-5'>
              <HookInput type='text' label='Template Name' name='name' />
              <HookReactSingleSelect label='Construction Type' name='constructionType' options={constructionType} />
              <HookReactSingleSelect label='Project Type' name='projectType' options={projectType} />
              <div className='flex gap-4 mt-5 justify-end'>
                <Button label='Clear' type='button' btnStyle='btn-neutral' onClick={handleFilterClear} />
                <Button label={'Filter'} type='submit' btnStyle='btn-primary' />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
    </>
  );
};

export default ProjectTemplatesListUI;
